import { jsx as _jsx } from "react/jsx-runtime";
import { ClipStatus, ClipType } from "@air/api/types";
import { Skeleton } from "@air/component-skeleton";
import { useBreakpointsContext } from "@air/provider-media-query";
import { tailwindMerge } from "@air/tailwind-variants";
import { ClipUtils } from "@air/utils-clips";
import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNoPreviewInVisualizer } from "~/hooks/useNoPreviewInVisualizer";
import { centralizedClipIsLoadingSelector, centralizedClipIsPDFTypeSelector, centralizedClipPDFAssetSelector, centralizedClipStatusSelector, centralizedClipTypeSelector } from "~/store/centralizedClip/selectors";
import { FileVisualizer } from "./components/FileVisualizer";
import { MobileImageVisualizer } from "./components/MobileImageVisualizer";
import { NoPreviewVisualizer } from "./components/NoPreviewVisualizer";
var VISUALIZER_TYPE;
(function(VISUALIZER_TYPE) {
    VISUALIZER_TYPE["VIDEO"] = "VIDEO";
    VISUALIZER_TYPE["PDF"] = "PDF";
    VISUALIZER_TYPE["IMAGE"] = "IMAGE";
    VISUALIZER_TYPE["LOADER"] = "LOADER";
    VISUALIZER_TYPE["NOT_VIEWABLE"] = "NOT_VIEWABLE";
    VISUALIZER_TYPE["PROCESSING"] = "PROCESSING";
    VISUALIZER_TYPE["FILE"] = "FILE";
})(VISUALIZER_TYPE || (VISUALIZER_TYPE = {}));
export const Visualizer = /*#__PURE__*/ memo((param)=>{
    let { VideoVisualizer, ImageVisualizer, PDFVisualizer } = param;
    const isLoading = useSelector(centralizedClipIsLoadingSelector);
    const clipType = useSelector(centralizedClipTypeSelector);
    const clipStatus = useSelector(centralizedClipStatusSelector);
    const clipPDFAsset = useSelector(centralizedClipPDFAssetSelector);
    const { isAboveMediumScreen } = useBreakpointsContext();
    const isPdfRenderedFile = useSelector(centralizedClipIsPDFTypeSelector);
    const { noPreviewInVisualizer } = useNoPreviewInVisualizer();
    const visualizerType = useMemo(()=>{
        if (isLoading) {
            return "LOADER";
        }
        if (!clipType) {
            return null;
        }
        if (isPdfRenderedFile) {
            if (!!clipPDFAsset) {
                return "PDF";
            } else {
                return "PROCESSING";
            }
        }
        if (clipType === ClipType.nonMedia || clipStatus === ClipStatus.nonTranscodable) {
            return "FILE";
        }
        if (ClipUtils.isFailed(clipStatus)) {
            return "NOT_VIEWABLE";
        }
        /**
     * Show the processing state for all processing nonvideo assets
     * Show processing state for videos if video url isn't returned from websocket yet
     * When it is returned we know we have watchable video while the rest processes
     *  */ if (noPreviewInVisualizer) {
            return "PROCESSING";
        }
        if (clipType === ClipType.photo || clipType === ClipType.animated) {
            return "IMAGE";
        }
        return "VIDEO";
    }, [
        clipPDFAsset,
        clipStatus,
        clipType,
        isLoading,
        isPdfRenderedFile,
        noPreviewInVisualizer
    ]);
    const clipVisual = useMemo(()=>{
        switch(visualizerType){
            case "LOADER":
                {
                    return /*#__PURE__*/ _jsx(Skeleton, {
                        width: 300
                    });
                }
            case "FILE":
                {
                    return /*#__PURE__*/ _jsx(FileVisualizer, {});
                }
            case "NOT_VIEWABLE":
                {
                    return /*#__PURE__*/ _jsx(NoPreviewVisualizer, {});
                }
            case "PDF":
                {
                    return /*#__PURE__*/ _jsx(PDFVisualizer, {});
                }
            case "PROCESSING":
                {
                    return /*#__PURE__*/ _jsx(NoPreviewVisualizer, {
                        isProcessing: true
                    });
                }
            case "IMAGE":
                {
                    if (!isAboveMediumScreen) return /*#__PURE__*/ _jsx(MobileImageVisualizer, {});
                    return /*#__PURE__*/ _jsx(ImageVisualizer, {});
                }
            case "VIDEO":
                {
                    return VideoVisualizer ? /*#__PURE__*/ _jsx(VideoVisualizer, {}) : null;
                }
            default:
                return null;
        }
    }, [
        visualizerType,
        PDFVisualizer,
        isAboveMediumScreen,
        ImageVisualizer,
        VideoVisualizer
    ]);
    const isPDF = visualizerType === "PDF";
    const isVideo = visualizerType === "VIDEO";
    return /*#__PURE__*/ _jsx("div", {
        className: tailwindMerge("group/visualizer relative flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden p-6", isVideo && "pb-3 pl-6 pr-6 pt-6", (!isAboveMediumScreen || isPDF) && "p-0"),
        children: /*#__PURE__*/ _jsx("div", {
            className: "flex size-full items-center justify-center",
            children: clipVisual
        })
    });
});
Visualizer.displayName = "Visualizer";
